import { Button, Col, Icon, Input, Modal, Row, Table } from "antd";
import Column from "antd/lib/table/Column";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Creators as SupplierActions } from "../../../store/ducks/supplier";
import {
  getOnlyNumber,
  phoneMask,
} from "../../../services/helpersMethodsService";
import { searchSupplierByName } from "../../../services/supplierService";
import useWindowSize from "../../../hooks/useWindowSize";

const DRAWER_ACTION_TYPE_MAP = {
  Supplement: "new_supplement_supplier",
  Others: "new_others_supplier",
  VeterinarySupplement: "new_veterinary_supplement_supplier",
  CattleFarmer: "new_animal_supplier",
};

function ModalSelectSupplier({
  pageData,
  visible,
  handleSelect,
  handleCancel,
}) {
  const { width } = useWindowSize();
  const [selectedRowsKeys, setSelectedRowKeys] = useState([]);
  const handleToggleRow = (record) => {
    setSelectedRowKeys((old) => (old.includes(record.id) ? [] : [record.id]));
  };
  const onSelectRowTable = {
    type: "checkbox",
    selectedRowKeys: selectedRowsKeys,
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      if (selected) {
        setSelectedRowKeys((old) => [record.id]);
      } else {
        setSelectedRowKeys((old) => old.filter((r) => r !== record.id));
      }
    },
    onSelectAll: (record, selected, selectedRows, nativeEvent) => {
      // Get just the ids
      const keys = selectedRows.map((sr) => sr.id);
      // Get all the selectedAnimalsKeys unless the unselected
      const lastSelectedAnimalsKeys = selectedRowsKeys.filter(
        (sAK) => !keys.includes(sAK),
      );
      if (record) {
        setSelectedRowKeys((old) => [...keys]);
      } else {
        setSelectedRowKeys(lastSelectedAnimalsKeys);
      }
    },
  };
  const { translation } = useSelector((state) => state.app);

  useEffect(() => {
    if (!visible) {
      setSelectedRowKeys([]);
    }
  }, [visible]);

  return (
    <Modal
      width={width >= 1440 ? 1000 : 800}
      title={translation.supplier.modalTitle}
      zIndex={1012}
      centered
      visible={visible}
      okText={translation.buttons.confirm}
      onCancel={handleCancel}
      onOk={() =>
        handleSelect(
          selectedRowsKeys.length > 0
            ? selectedRowsKeys[selectedRowsKeys.length - 1]
            : null,
        )
      }
    >
      <Table
        rowKey="id"
        size="small"
        rowSelection={onSelectRowTable}
        dataSource={pageData != null ? pageData : []}
        onRow={(record) => ({
          onClick: (event) => {
            if (event.target.closest(".ant-table-selection-column")) return;
            handleToggleRow(record);
          },
          style: { cursor: "pointer" },
        })}
        scroll={{
          x: true,
        }}
        style={{ width: "100%", margin: "0" }}
      >
        <Column
          title={translation.supplier.table.columns.name}
          dataIndex="name"
          align="left"
          key="name"
        />
        <Column
          title={translation.supplier.table.columns.type}
          dataIndex="type"
          align="left"
          key="type"
          render={(text, record) =>
            record.type === "Supplement"
              ? translation.supplier.types.supplement
              : record.type === "Technology"
                ? translation.supplier.types.technology
                : record.type === "CattleFarmer"
                  ? translation.supplier.types.cattleFarmer
                  : record.type === "VeterinarySupplement"
                    ? translation.supplier.types.veterinarySupplement
                    : record.type === "Others"
                      ? translation.supplier.types.others
                      : ""
          }
        />
        <Column
          title={translation.supplier.table.columns.phoneNumber}
          dataIndex="phoneNumber"
          align="left"
          key="phoneNumber"
          render={(text, record) =>
            phoneMask(getOnlyNumber(record?.phoneNumber))
          }
        />
        <Column
          title={translation.supplier.table.columns.email}
          dataIndex="email"
          align="left"
          key="email"
        />
        <Column
          title={translation.supplier.table.columns.city_uf}
          dataIndex="city"
          align="left"
          key="city"
          render={(text, record) =>
            record.city != null
              ? `${record?.city?.name}/${record?.city?.state?.name}`
              : ""
          }
        />
      </Table>
    </Modal>
  );
}

function SearchSupplier({
  textSearch,
  setTextSearch,
  value,
  handleChange,
  handleDeselect,
  showDeselect = false,
  type = null,
  disabled = false,
}) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const { newSupplier, shouldRefreshListDropDown } = useSelector(
    (state) => state.supplier,
  );
  const dispatch = useDispatch();
  const awaitingCreate = useRef(false);

  const handleSearch = useCallback(async () => {
    if (textSearch) {
      setIsLoading(true);
      try {
        const {
          data: { results },
        } = await searchSupplierByName({
          groupId,
          farmId,
          name: textSearch,
          type,
        });
        setData(results?.content ?? []);
        setModalVisible(true);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    } else {
      setData([]);
    }
  }, [farmId, groupId, textSearch, type]);

  const handleCancelModal = () => {
    setModalVisible(false);
    setData([]);
    setTextSearch(null);
    handleChange(null, null);
  };

  const handleSelectModal = (selectedId) => {
    setModalVisible(false);
    const supplierSelected = data.find((s) => s.id === selectedId);
    if (!supplierSelected) return;
    setTextSearch(supplierSelected.name);
    handleChange(selectedId, supplierSelected);
  };

  const handleDeselectModal = () => {
    setModalVisible(false);
    const supplierSelected = data.find((s) => s.id === value);
    setTextSearch(null);
    setData([]);
    handleDeselect(supplierSelected);
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // prevent form submission
      handleSearch();
    }
  };

  const createNewSupplier = useCallback(() => {
    awaitingCreate.current = true;
    const drawerActionType = DRAWER_ACTION_TYPE_MAP[type] ?? "new";
    dispatch(SupplierActions.showDrawer(drawerActionType));
  }, [dispatch, type]);

  useEffect(() => {
    if (awaitingCreate.current && newSupplier?.id) {
      setTextSearch(newSupplier.name);
      handleChange(newSupplier.id, newSupplier);
      awaitingCreate.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshListDropDown]);

  return (
    <div>
      <Row type="flex" gutter={8}>
        <Col span={showDeselect ? 16 : 18}>
          <Input
            placeholder={translation.defaultPlaceholder}
            disabled={isLoading || disabled}
            value={textSearch ?? ""}
            onKeyDown={handleEnter}
            onChange={(e) => {
              setTextSearch(e.target.value);
              if (value) handleChange(null, null);
            }}
          />
        </Col>
        <Col span={2}>
          {isLoading ? (
            <Icon type="loading" />
          ) : (
            <Button
              shape="circle"
              icon="search"
              disabled={disabled}
              onClick={handleSearch}
            />
          )}
        </Col>
        {showDeselect && value && (
          <Col span={2}>
            {!isLoading && (
              <Button
                shape="circle"
                icon="close"
                disabled={disabled}
                onClick={handleDeselectModal}
              />
            )}
          </Col>
        )}
        <Col span={2}>
          <Button
            shape="circle"
            icon="plus"
            disabled={disabled}
            onClick={createNewSupplier}
          />
        </Col>
      </Row>
      <ModalSelectSupplier
        pageData={data}
        visible={modalVisible}
        handleSelect={handleSelectModal}
        handleCancel={handleCancelModal}
      />
    </div>
  );
}

export default SearchSupplier;
